import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  message,
  Row,
  Col,
  Statistic,
  Progress,
  Tabs,
  Badge,
  Tooltip,
  Popconfirm,
  Empty,
  Alert
} from 'antd';
import {
  CrownOutlined,
  UserOutlined,
  SettingOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  SearchOutlined,
  ReloadOutlined,
  BarChartOutlined,
  TeamOutlined,
  KeyOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import {
  getAllPermissions,
  getUserPermission,
  setUserPermission,
  deleteUserPermission,
  getPermissionStats,
  getUnassignedUsers,
  clearMessages,
  updateFilters,
  clearFilters,
  openEditModal,
  closeEditModal,
  openDeleteModal,
  closeDeleteModal,
  updateFormData,
  resetForm
} from '../../features/account/permissionSlice';

const { Option } = Select;
const { TextArea } = Input;
const { TabPane } = Tabs;

const Omega = () => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = useState('1');

  const {
    permissions,
    pagination,
    currentUserPermission,
    unassignedUsers,
    unassignedPagination,
    stats,
    loading,
    statsLoading,
    userPermissionLoading,
    unassignedLoading,
    error,
    success,
    filters,
    editModalVisible,
    deleteModalVisible,
    selectedUserId,
    formData
  } = useSelector((state) => state.permission);

  useEffect(() => {
    dispatch(getAllPermissions());
    dispatch(getPermissionStats());
    dispatch(getUnassignedUsers());
  }, [dispatch]);

  useEffect(() => {
    if (success) {
      message.success(success);
      dispatch(clearMessages());
    }
  }, [success, dispatch]);

  useEffect(() => {
    if (error) {
      message.error(error);
      dispatch(clearMessages());
    }
  }, [error, dispatch]);

  // Update form when formData changes
  useEffect(() => {
    form.setFieldsValue(formData);
  }, [formData, form]);

  const handleTableChange = (pagination, filters, sorter) => {
    dispatch(getAllPermissions({
      page: pagination.current,
      limit: pagination.pageSize,
      ...filters
    }));
  };

  const handleSearch = (value) => {
    dispatch(updateFilters({ search: value }));
    dispatch(getAllPermissions({ 
      page: 1, 
      search: value,
      level: filters.level 
    }));
  };

  const handleLevelFilter = (level) => {
    dispatch(updateFilters({ level }));
    dispatch(getAllPermissions({ 
      page: 1, 
      level,
      search: filters.search 
    }));
  };

  const handleEditPermission = (user, permission = null) => {
    dispatch(openEditModal({ userId: user._id, permission }));
    if (permission) {
      // If editing existing permission, load current data
      dispatch(getUserPermission(user._id));
    }
  };

  const handleSavePermission = async () => {
    try {
      const values = await form.validateFields();
      await dispatch(setUserPermission({
        userId: selectedUserId,
        permissionData: values
      }));
      
      // Refresh data
      dispatch(getAllPermissions({ page: pagination.currentPage }));
      dispatch(getPermissionStats());
      dispatch(getUnassignedUsers());
      
      form.resetFields();
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const handleDeletePermission = async (reason) => {
    await dispatch(deleteUserPermission({
      userId: selectedUserId,
      reason
    }));
    
    // Refresh data
    dispatch(getAllPermissions({ page: pagination.currentPage }));
    dispatch(getPermissionStats());
  };

  const handleRefresh = () => {
    dispatch(getAllPermissions({ page: pagination.currentPage }));
    dispatch(getPermissionStats());
    dispatch(getUnassignedUsers());
  };

  const getLevelColor = (level) => {
    const colors = {
      1: 'default',
      2: 'green',
      3: 'blue',
      4: 'orange',
      5: 'red'
    };
    return colors[level] || 'default';
  };

  const getLevelName = (level) => {
    const names = {
      1: 'Basic',
      2: 'Limited',
      3: 'Standard',
      4: 'Advanced',
      5: 'Full'
    };
    return names[level] || 'Unknown';
  };

  const permissionColumns = [
    {
      title: 'User',
      render: (_, record) => (
        <div>
          <div className="font-medium flex items-center">
            <UserOutlined className="mr-2 text-gray-500" />
            {record.userId?.name || 'Unknown User'}
          </div>
          <div className="text-xs text-gray-500">
            {record.userId?.email}
          </div>
          <Tag size="small" color="blue">
            {record.userId?.role}
          </Tag>
        </div>
      ),
    },
    {
      title: 'Permission Level',
      dataIndex: 'level',
      render: (level, record) => (
        <div className="text-center">
          <Tag 
            color={getLevelColor(level)} 
            icon={<KeyOutlined />}
            className="mb-1"
          >
            Level {level} - {getLevelName(level)}
          </Tag>
          <div className="text-xs text-gray-500">
            {record.description}
          </div>
        </div>
      ),
    },
    {
      title: 'Set By',
      render: (_, record) => (
        <div>
          <div className="text-sm font-medium">
            {record.setBy?.name || 'System'}
          </div>
          <div className="text-xs text-gray-500">
            {new Date(record.updatedAt).toLocaleString()}
          </div>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit Permission">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEditPermission(record.userId, record)}
            />
          </Tooltip>
          <Tooltip title="Reset to Default">
            <Popconfirm
              title="Reset Permission"
              description="Are you sure you want to reset this user's permission to default level?"
              onConfirm={() => {
                dispatch(openDeleteModal({ userId: record.userId._id }));
              }}
              okText="Yes"
              cancelText="No"
            >
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const unassignedColumns = [
    {
      title: 'User',
      render: (_, record) => (
        <div>
          <div className="font-medium flex items-center">
            <UserOutlined className="mr-2 text-gray-500" />
            {record.name}
          </div>
          <div className="text-xs text-gray-500">{record.email}</div>
          <Tag size="small" color="blue">{record.role}</Tag>
        </div>
      ),
    },
    {
      title: 'Joined',
      dataIndex: 'date',
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Default Level',
      render: () => (
        <Tag color="default" icon={<KeyOutlined />}>
          Level 1 - Basic
        </Tag>
      ),
    },
    {
      title: 'Actions',
      render: (_, record) => (
        <Button
          size="small"
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => handleEditPermission(record)}
        >
          Assign Permission
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="shadow-soft border border-border-light rounded-xl">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center">
              <CrownOutlined className="text-xl text-white" />
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-1">Omega User Management</h3>
              <p className="text-gray-600">Manage omega user permissions and access levels</p>
            </div>
          </div>
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            loading={loading || statsLoading}
          >
            Refresh
          </Button>
        </div>

        {/* Quick Stats */}
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={6}>
            <Statistic
              title="Total Omega Users"
              value={stats.totalUsers}
              prefix={<TeamOutlined />}
              loading={statsLoading}
            />
          </Col>
          <Col xs={12} sm={6}>
            <Statistic
              title="With Permissions"
              value={stats.totalPermissions}
              prefix={<KeyOutlined />}
              loading={statsLoading}
            />
          </Col>
          <Col xs={12} sm={6}>
            <Statistic
              title="Unassigned Omega"
              value={stats.usersWithoutPermissions}
              prefix={<ExclamationCircleOutlined />}
              loading={statsLoading}
            />
          </Col>
          <Col xs={12} sm={6}>
            <Statistic
              title="Recent Activity"
              value={stats.recentActivity?.length || 0}
              prefix={<BarChartOutlined />}
              loading={statsLoading}
            />
          </Col>
        </Row>
      </Card>

      {/* Level Distribution */}
      <Card 
        title="Omega User Permission Level Distribution" 
        className="shadow-soft border border-border-light rounded-xl"
      >
        <Row gutter={[16, 16]}>
          {Object.entries(stats.levelDistribution || {}).map(([level, count]) => (
            <Col key={level} xs={12} sm={8} md={4}>
              <Card size="small" className="text-center">
                <div className="mb-2">
                  <Tag color={getLevelColor(parseInt(level))} size="large">
                    Level {level}
                  </Tag>
                </div>
                <div className="text-lg font-bold">{count}</div>
                <div className="text-xs text-gray-500">{getLevelName(parseInt(level))}</div>
              </Card>
            </Col>
          ))}
        </Row>
      </Card>

      {/* Main Content Tabs */}
      <Card className="shadow-soft border border-border-light rounded-xl">
        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
          items={[
            {
              key: '1',
              label: (
                <span>
                  <SettingOutlined />
                  Omega Users with Permissions
                  <Badge count={stats.totalPermissions} className="ml-2" />
                </span>
              ),
              children: (
                <div className="space-y-4">
                  {/* Filters */}
                  <div className="flex flex-wrap gap-4 items-center">
                    <Input.Search
                      placeholder="Search omega users by name or email"
                      style={{ width: 300 }}
                      onSearch={handleSearch}
                      prefix={<SearchOutlined />}
                    />
                    <Select
                      placeholder="Filter by level"
                      style={{ width: 200 }}
                      value={filters.level}
                      onChange={handleLevelFilter}
                      allowClear
                    >
                      {[1, 2, 3, 4, 5].map(level => (
                        <Option key={level} value={level}>
                          Level {level} - {getLevelName(level)}
                        </Option>
                      ))}
                    </Select>
                    <Button onClick={() => {
                      dispatch(clearFilters());
                      dispatch(getAllPermissions({ page: 1 }));
                    }}>
                      Clear Filters
                    </Button>
                  </div>

                  {/* Permissions Table */}
                  <Table
                    dataSource={permissions}
                    columns={permissionColumns}
                    loading={loading}
                    rowKey="_id"
                    onChange={handleTableChange}
                    pagination={{
                      current: pagination.currentPage,
                      total: pagination.totalPermissions,
                      pageSize: pagination.limit,
                      showSizeChanger: true,
                      showQuickJumper: true,
                      showTotal: (total, range) =>
                        `${range[0]}-${range[1]} of ${total} permissions`,
                    }}
                  />
                </div>
              )
            },
            {
              key: '2',
              label: (
                <span>
                  <UserOutlined />
                  Unassigned Omega Users
                  <Badge count={stats.usersWithoutPermissions} className="ml-2" />
                </span>
              ),
              children: (
                <div className="space-y-4">
                  <Alert
                    message="Unassigned Omega Users"
                    description="These omega users don't have specific permission levels assigned and are using the default Level 1 (Basic) permissions."
                    type="info"
                    showIcon
                  />

                  <Table
                    dataSource={unassignedUsers}
                    columns={unassignedColumns}
                    loading={unassignedLoading}
                    rowKey="_id"
                    pagination={{
                      current: unassignedPagination.currentPage,
                      total: unassignedPagination.totalUsers,
                      pageSize: unassignedPagination.limit,
                      showSizeChanger: true,
                      showTotal: (total, range) =>
                        `${range[0]}-${range[1]} of ${total} unassigned omega users`,
                    }}
                  />
                </div>
              )
            }
          ]}
        />
      </Card>

      {/* Edit Permission Modal */}
      <Modal
        title={currentUserPermission ? "Edit Omega User Permission" : "Assign Omega User Permission"}
        open={editModalVisible}
        onOk={handleSavePermission}
        onCancel={() => dispatch(closeEditModal())}
        confirmLoading={loading}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={formData}
          onValuesChange={(changed, all) => dispatch(updateFormData(all))}
        >
          <Form.Item
            label="Permission Level"
            name="level"
            rules={[{ required: true, message: 'Please select a permission level' }]}
          >
            <Select size="large">
              {[1, 2, 3, 4, 5].map(level => (
                <Option key={level} value={level}>
                  <div className="flex items-center justify-between">
                    <span>Level {level} - {getLevelName(level)}</span>
                    <Tag color={getLevelColor(level)} size="small">
                      {level === 1 ? 'Basic' : level === 5 ? 'Full Access' : 'Custom'}
                    </Tag>
                  </div>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Notes"
            name="notes"
            help="Optional notes about this permission assignment"
          >
            <TextArea 
              rows={3} 
              placeholder="Add any relevant notes about this permission level..."
              maxLength={500}
            />
          </Form.Item>

          <Form.Item
            label="Reason for Change"
            name="reason"
            help="Explain why this permission level is being assigned"
          >
            <TextArea 
              rows={2} 
              placeholder="Reason for this permission change..."
              maxLength={200}
            />
          </Form.Item>
        </Form>

        {currentUserPermission && currentUserPermission.history && (
          <div className="mt-4 p-3 bg-gray-50 rounded">
            <h4 className="font-medium mb-2">Permission History</h4>
            <div className="max-h-32 overflow-y-auto space-y-1">
              {currentUserPermission.history.slice(0, 5).map((change, index) => (
                <div key={index} className="text-xs">
                  <span className="font-medium">
                    Level {change.previousLevel} → Level {change.newLevel}
                  </span>
                  <span className="text-gray-500 ml-2">
                    {new Date(change.timestamp).toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        title="Reset Omega User Permission"
        open={deleteModalVisible}
        onOk={() => {
          const reason = form.getFieldValue('deleteReason') || 'Permission reset by omega user';
          handleDeletePermission(reason);
        }}
        onCancel={() => dispatch(closeDeleteModal())}
        confirmLoading={loading}
        okText="Reset to Default"
        okType="danger"
      >
        <div className="space-y-4">
          <Alert
            message="This will reset the omega user's permission to Level 1 (Basic)"
            description="The omega user will lose their current permission level and return to default basic access."
            type="warning"
            showIcon
          />
          
          <Form.Item
            label="Reason for Reset"
            name="deleteReason"
          >
            <TextArea 
              rows={3} 
              placeholder="Explain why this permission is being reset..."
            />
          </Form.Item>
        </div>
      </Modal>
    </div>
  );
};

export default Omega;